import { useCallback, useEffect, useMemo, useState } from 'react';

const normalizePercentage = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return parsed;
};

const normalizePayload = (values = {}) => ({
  nombre: (values.nombre || '').trim(),
  descripcion: (values.descripcion || '').trim(),
  activo: Boolean(values.activo),
  ajuste_forecast_pct: normalizePercentage(values.ajuste_forecast_pct)
});

export const useAdminCategories = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const fetchList = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/admin/api/categories', { signal });
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || 'No se pudieron obtener las categorías');
      }
      if (!signal?.aborted) {
        setItems(data.categories || []);
      }
    } catch (err) {
      if (!signal?.aborted) {
        setError(err?.message || 'Error al cargar categorías');
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchList(controller.signal);
    return () => controller.abort();
  }, [fetchList, refreshToken]);

  const refresh = useCallback(() => {
    setRefreshToken((value) => value + 1);
  }, []);

  const saveCategory = useCallback(async (values) => {
    const payload = normalizePayload(values);
    if (!payload.nombre) {
      throw new Error('El nombre de la categoría es requerido.');
    }
    const hasId = Boolean(values?.id);
    const url = hasId ? `/admin/api/categories/${values.id}` : '/admin/api/categories';
    const method = hasId ? 'PUT' : 'POST';
    const response = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo guardar la categoría');
    }
    return data;
  }, []);

  const deleteCategory = useCallback(async (id) => {
    const response = await fetch(`/admin/api/categories/${id}`, { method: 'DELETE' });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo eliminar la categoría');
    }
    return data;
  }, []);

  return useMemo(() => ({
    items,
    loading,
    error,
    refresh,
    saveCategory,
    deleteCategory
  }), [items, loading, error, refresh, saveCategory, deleteCategory]);
};
